/**
 * useDependentOptions — T12.D.5.
 *
 * Hook zwracający funkcję, która dla danego pola zwraca to samo pole
 * z przefiltrowanymi opcjami — zgodnie z wartością pola-rodzica.
 *
 * Użycie:
 *   const filterField = useDependentOptions(data);
 *   const fieldWithFiltered = filterField(field);
 *   <FieldRegistry.renderInput field={fieldWithFiltered} ... />
 *
 * Logika:
 *  - Jeśli pole nie ma `dependsOnFieldSlug` → zwróć pole bez zmian
 *  - Jeśli pole-rodzic nie ma wartości → pokaż wszystkie opcje (nie filtruj)
 *  - Jeśli pole-rodzic ma wartość → filtruj opcje wg `dependsOnOptionMap[parentValue]`
 *  - Jeśli parentValue nie ma wpisu w mapie → pokaż wszystkie opcje
 */

import { useCallback } from 'react';

import type { ModuleField } from '@/components/fields/FieldRegistry';

export function useDependentOptions(
  data: Record<string, unknown>,
): (field: ModuleField) => ModuleField {
  return useCallback(
    (field: ModuleField): ModuleField => {
      if (!field.dependsOnFieldSlug || !field.dependsOnOptionMap) return field;

      const parentValue = data[field.dependsOnFieldSlug];
      if (parentValue == null || parentValue === '') return field;

      const parentStr = Array.isArray(parentValue)
        ? String(parentValue[0] ?? '')
        : String(parentValue);

      const allowedValues = field.dependsOnOptionMap[parentStr];
      if (!allowedValues) return field; // brak wpisu w mapie → bez filtrowania

      return {
        ...field,
        options: field.options?.filter((o) => allowedValues.includes(o.value)) ?? [],
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );
}

/**
 * Sprawdza czy podana wartość jest nadal dostępna po przefiltrowaniu.
 * Zwraca null gdy wartość zniknęła z dostępnych opcji.
 */
export function clearIfUnavailable(value: unknown, filteredField: ModuleField): unknown {
  if (!filteredField.options || value == null) return value;
  const allowed = filteredField.options.map((o) => o.value);
  if (Array.isArray(value)) {
    const filtered = value.filter((v) => allowed.includes(String(v)));
    return filtered.length === value.length ? value : filtered;
  }
  return allowed.includes(String(value)) ? value : null;
}
